import type { CSSProperties } from "react";
import type { StickyNoteVote } from "../../types/domain";
import { paperColorByKey, pinColorByKey } from "../../util/boardConstants";
import { getUserColor } from "../../util/UserColor";

interface StickyNoteViewProps {
  authorName: string;
  imageData: string;
  paperColor: string;
  pinColor: string;
  showDelete?: boolean;
  onDelete?: () => void;
  showRotate?: boolean;
  onRotate?: () => void;
  rotateTitle?: string;
  /** Poll notes get a voting strip below the drawing. */
  poll?: boolean;
  votes?: StickyNoteVote[];
  /** Called with "YES"/"NO" to cast/change a vote, null to retract it. Omit to render read-only. */
  onVote?: (vote: "YES" | "NO" | null) => void;
  voteTitles?: { yes: string; no: string; retract: string };
  className?: string;
}

/**
 * Presentational sticky note (paper + pin + author + drawing + optional poll strip).
 * Positioning, rotation and pointer interactions are the parent's responsibility.
 */
function StickyNoteView({
  authorName,
  imageData,
  paperColor,
  pinColor,
  showDelete,
  onDelete,
  showRotate,
  onRotate,
  rotateTitle,
  poll,
  votes = [],
  onVote,
  voteTitles,
  className,
}: StickyNoteViewProps) {
  const paper = paperColorByKey(paperColor);
  const pin = pinColorByKey(pinColor);
  const style = {
    "--paper-base": paper.base,
    "--paper-edge": paper.edge,
    "--pin-color": pin.color,
  } as CSSProperties;

  const ownVote = votes.find((v) => v.own)?.vote ?? null;

  const renderPollZone = (side: "YES" | "NO") => {
    const sideVotes = votes.filter((v) => v.vote === side);
    const isOwnSide = ownVote === side;
    const title = !onVote ? undefined : isOwnSide ? voteTitles?.retract : side === "YES" ? voteTitles?.yes : voteTitles?.no;
    return (
      <button
        type="button"
        className={`sticky-note-poll-zone ${side.toLowerCase()}${isOwnSide ? " voted" : ""}`}
        disabled={!onVote}
        title={title}
        // Keep the board's pan/drag handlers from swallowing the tap.
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onVote?.(isOwnSide ? null : side);
        }}
      >
        <span className="sticky-note-poll-header">
          {side === "YES" ? "✅" : "❌"} {sideVotes.length}
        </span>
        <span className="sticky-note-poll-dots">
          {sideVotes.map((v) => (
            <span
              key={v.userId}
              className={`sticky-note-poll-dot${v.own ? " own" : ""}`}
              title={v.name}
              style={{ background: getUserColor(v.userId) }}
            />
          ))}
        </span>
      </button>
    );
  };

  return (
    <div className={`sticky-note${poll ? " sticky-note--poll" : ""}${className ? ` ${className}` : ""}`} style={style}>
      <div className="sticky-note-pin" />
      {showRotate && (
        <button
          type="button"
          className="sticky-note-rotate"
          title={rotateTitle}
          aria-label={rotateTitle ?? "rotate"}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onRotate?.();
          }}
        >
          ↻
        </button>
      )}
      <div className="sticky-note-author" title={authorName}>{authorName}</div>
      <img className="sticky-note-image" src={imageData} alt="" draggable={false} />
      {poll && (
        <div className="sticky-note-poll">
          {renderPollZone("YES")}
          {renderPollZone("NO")}
        </div>
      )}
      {showDelete && (
        <button
          type="button"
          className="sticky-note-delete"
          aria-label="delete"
          // Keep the board's pan/drag handlers from swallowing the tap.
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.();
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

export default StickyNoteView;
